import { Skeleton } from "@mui/material";
import { ArrowRight, Banknote, Briefcase, MapPin } from "lucide-react";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import HtmlRenderer from "../components/HtmlRenderer";
import { ClickableContainer, PseudoLink } from "../components/StyledComponents";
import JobsDetails from "../DetailFunctions/JobsDetails";
import useFetchConfig from "../fetchFunction/ConfigFetch";
import useFetchPaginatedElements from "../fetchFunction/OverviewFetch";
import Pagination from "../Pagination";
import "../styles/JobsOverview.css";

const validateJobs = (data) => {
  if (!Array.isArray(data) || data.length === 0) {
    console.warn("No valid jobs found in data:", data);

    return [];
  }

  return data
    .map((item) => {
      const id = item?.id || null;
      const headline = item?.headline?.trim() || "";
      const text = item?.text?.trim() || "Keine Beschreibung verfügbar";
      const imageUrl = item?.image_urls || null;
      const location = item?.location || "Kein Standort verfügbar";
      const salaryRange = item?.salary_range || "";
      const employmentType = item?.employment_type || "Unbekannt";
      const company = item?.company || "Unbekannt";
      const iconUrl = item?.iconUrl || null;
      const portalUrl = item?.portal_url || "#";

      return {
        id,
        headline,
        text,
        imageUrl,
        location,
        salaryRange,
        employmentType,
        company,
        iconUrl,
        portalUrl,
      };
    })
    .filter(Boolean);
};

const JobsOverview = ({ styling, companyAccessname }) => {
  const { configSettings } = useFetchConfig();

  const currentConfig = configSettings?.navbarItems?.find(
    (item) => item.name === "Jobs"
  );
  const pageSize = configSettings?.pageSize;
  const displayLimit = currentConfig?.widgetDisplayLimit || pageSize;

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [showDetailPage, setShowDetailPage] = useState(false);

  const {
    loading,
    error,
    totalPages,
    jobs: jobData,
  } = useFetchPaginatedElements(
    companyAccessname,
    "jobs",
    currentPage,
    pageSize
  );

  useEffect(() => {
    if (!showDetailPage) {
      window.scrollTo(0, 0);
    }
  }, [showDetailPage]);

  useEffect(() => {}, [jobData]);

  const validJobs = validateJobs(jobData).slice(0, displayLimit);

  const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;

    return text.substring(0, maxLength) + "...";
  };

  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  if (loading) {
    return (
      <div>
        {Array.from(new Array(displayLimit)).map((_, index) => (
          <ClickableContainer key={index} styling={styling}>
            <Skeleton
              variant="text"
              width="80%"
              height={30}
              style={{ marginBottom: "10px" }}
              animation="wave"
            />
            <Skeleton
              variant="rectangular"
              width="100px"
              height="100px"
              style={{ marginBottom: "10px" }}
              animation="wave"
            />
            <Skeleton
              variant="text"
              width="80%"
              style={{ marginBottom: "10px" }}
              animation="wave"
            />
            <Skeleton
              variant="rectangular"
              width="60%"
              height={30}
              animation="wave"
            />
          </ClickableContainer>
        ))}
      </div>
    );
  }

  if (error) {
    console.error("Error loading jobs:", error);

    return <p style={{ color: styling.textColor }}>Fehler: {error}</p>;
  }

  if (validJobs.length === 0) {
    console.warn("No valid jobs to display");

    return <p style={{ color: styling.textColor }}>Keine Jobs verfügbar.</p>;
  }

  return (
    <div>
      {!showDetailPage ? (
        <>
          {validJobs.map((job, index) => (
            <ClickableContainer
              key={index}
              styling={styling}
              onClick={() => {
                console.log("Selected Job ID:", job.id);
                setSelectedJobId(job.id);
                setShowDetailPage(true);
              }}
            >
              <h3
                className="chapter-title"
                style={{ color: styling.textColor }}
              >
                {job.headline}
              </h3>
              <div className="job-content">
                {job.imageUrl && (
                  <img
                    src={job.imageUrl}
                    alt={job.headline || "Job image"}
                    className="job-image"
                  />
                )}
                <div style={{ color: styling.textColor }}>
                  <HtmlRenderer
                    texts={[truncateText(job.text, 150)]}
                    sanitizeHtml={true}
                  />
                </div>
              </div>
              <div className="job-details">
                {job.location && (
                  <p
                    className="job-location"
                    style={{ color: styling.textColor }}
                  >
                    <MapPin
                      size={16}
                      className="job-icon"
                      style={{ color: styling.iconColor }}
                    />
                    {job.location}
                  </p>
                )}
                {job.salaryRange && (
                  <p
                    className="job-salary"
                    style={{ color: styling.textColor }}
                  >
                    <Banknote
                      size={16}
                      className="job-icon"
                      style={{ color: styling.iconColor }}
                    />
                    {job.salaryRange}
                  </p>
                )}
                {job.company && (
                  <p
                    className="job-company"
                    style={{ color: styling.textColor }}
                  >
                    <Briefcase
                      size={16}
                      className="job-icon"
                      style={{ color: styling.iconColor }}
                    />
                    {job.company}
                  </p>
                )}
              </div>
              <PseudoLink className="pseudo-link" styling={styling}>
                Details{" "}
                <ArrowRight
                  size={16}
                  className="arrow-icon"
                  style={{ color: styling.iconColor }}
                />
              </PseudoLink>
            </ClickableContainer>
          ))}
          <div className="pagination-container">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              styling={styling}
              onChangePage={goToPage}
            />
          </div>
        </>
      ) : (
        <JobsDetails
          jobId={selectedJobId}
          styling={styling}
          onClose={() => setShowDetailPage(false)}
        />
      )}
    </div>
  );
};

JobsOverview.propTypes = {
  styling: PropTypes.shape({
    textColor: PropTypes.string,
    linkColor: PropTypes.string,
    linkHoverColor: PropTypes.string,
    chapterEntry: PropTypes.object,
    chapterTitle: PropTypes.object,
    iconColor: PropTypes.string,
  }),
  companyAccessname: PropTypes.string.isRequired,
};

export default JobsOverview;
